import React, {
  useState,
  useRef,
  useMemo,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { Table, Pagination, Modal } from "antd";
import api from "../api/index";
import { UserContext } from "../UserContext";
import "./styles/CsvUpload.css";
import { motion } from "framer-motion";

const badgeClass = (status = "") => {
  const normalized = status.toLowerCase();
  return `csv-status csv-status--${normalized}`;
};

const CsvUploadForm = () => {
  const { user } = useContext(UserContext);

  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadMessage, setUploadMessage] = useState("");
  const [uploads, setUploads] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const total = uploads.length;

  // ---------- fetch history (shared between effect + after upload) ----------

  const fetchHistory = useCallback(async () => {
    if (!user) return;

    try {
      setLoadingHistory(true);

      const url = `${api.main}api/agent/uploads/locations/history?sagent_id=${encodeURIComponent(
        user.id
      )}&limit=20&access_token=${user.access_token}`;

      const resp = await fetch(url);
      const data = await resp.json();

      if (resp.ok) {
        setUploads(data.uploads || []);
      } else {
        console.error("History fetch failed:", data);
      }
    } catch (e) {
      console.error("Error fetching history:", e);
    } finally {
      setLoadingHistory(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetchHistory();
  }, [user, fetchHistory]);

  // ---------- pagination ----------

  const safeCurrent = useMemo(() => {
    if (total === 0) return 1;
    const maxPage = Math.ceil(total / pageSize) || 1;
    return Math.min(current, maxPage);
  }, [current, pageSize, total]);

  const pageRows = useMemo(() => {
    if (total === 0) return [];
    const start = (safeCurrent - 1) * pageSize;
    const end = start + pageSize;
    return uploads.slice(start, end);
  }, [uploads, safeCurrent, pageSize, total]);

  // ---------- columns ----------

  const columns = [
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 220,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      render: (text) => (text ? new Date(text).toLocaleString() : "—"),
    },
    {
      title: "Filename",
      dataIndex: "filename",
      key: "filename",
      sorter: (a, b) => (a.filename || "").localeCompare(b.filename || ""),
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
      render: (status) => (
        <span className={badgeClass(status)}>{status || "—"}</span>
      ),
    },
    {
      title: "Success",
      dataIndex: "successful_rows",
      key: "successful_rows",
      width: 120,
      align: "right",
      sorter: (a, b) =>
        (a.successful_rows || 0) - (b.successful_rows || 0),
    },
    {
      title: "Errors",
      dataIndex: "error_rows",
      key: "error_rows",
      width: 100,
      align: "right",
      sorter: (a, b) => (a.error_rows || 0) - (b.error_rows || 0),
    },
    {
      title: "Error Details",
      key: "error_details",
      width: 140,
      render: (_, row) =>
        row.error_rows > 0 && row.error_file_url ? (
          <a href={row.error_file_url} download>
            Download
          </a>
        ) : (
          "—"
        ),
    },
  ];

  const selectedFileName = selectedFile ? selectedFile.name : null;

  // ---------- handlers ----------

  const handleFileInputChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setSelectedFile(file || null);
    setUploadMessage("");
  };

  const handleUploadClick = async () => {
    if (!user) {
      Modal.error({
        title: "Not signed in",
        content: "Please sign in again before uploading a CSV.",
      });
      return;
    }

    if (!selectedFile) {
      Modal.error({
        title: "No file selected",
        content: "Please choose a CSV file before clicking Upload.",
      });
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("id", user.id);
      formData.append("access_token", user.access_token);

      const resp = await fetch(
        `${api.main}api/agent/add_multiple_locations_submit`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await resp.json();

      if (!resp.ok) {
        throw new Error(data.error || "Upload failed");
      }

      setUploadMessage(data.message || "Upload successful.");
      setSelectedFile(null);

      // refresh history after upload
      await fetchHistory();
    } catch (e) {
      const msg = e.message || "Upload failed";
      setUploadMessage(msg);
      Modal.error({
        title: "Upload failed",
        content: msg,
      });
    } finally {
      setUploading(false);
    }
  };

  // ---------- render ----------

  return (
    <div className="csv-upload-page">
      <div className="page-header">
        <div>
          <p className="page-eyebrow">Locations</p>
          <h2>Bulk CSV Upload</h2>
          <p className="section-subtitle">
            Import up to 2,500 locations per file. CSV format required.
          </p>
        </div>
        <div className="page-pill">
          {uploads.length ? `${uploads.length} recent uploads` : "No uploads yet"}
        </div>
      </div>

      <section className="page-section csv-upload-panel">
        <div className="csv-upload-card">
          <div className="csv-upload-links">
            <a href="/Spare-Agent-Upload-Example-2025.csv" download>
              Download Sample CSV
            </a>
            <a href="/DocumentationAndRequirements.pdf" download>
              Download Documentation & Requirements
            </a>
          </div>

          <input
            ref={fileInputRef}
            className="file-input"
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            style={{ display: "none" }}
          />

          <div className="csv-upload-actions">
            <motion.button
              type="button"
              className="btn btn-primary"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              Choose CSV File
            </motion.button>
            <motion.button
              type="button"
              className="btn btn-primary"
              onClick={handleUploadClick}
              disabled={!selectedFile || uploading}
            >
              {uploading ? "Uploading..." : "Upload"}
            </motion.button>
          </div>

          {selectedFileName && (
            <div className="csv-selected-file">Selected file: {selectedFileName}</div>
          )}

          {uploadMessage && (
            <div className="csv-upload-message">{uploadMessage}</div>
          )}
        </div>

        <div className="csv-upload-card">
          <div className="section-heading">
            <div>
              <p className="section-eyebrow">History</p>
              <h3>Recent Uploads</h3>
            </div>
          </div>

          <div className="csv-history-table show-scrollbar">
            <Table
              className="csv-table"
              rowKey={(row) =>
                row.id || row.filename || `${row.created_at}-${Math.random()}`
              }
              columns={columns}
              dataSource={pageRows}
              loading={loadingHistory}
              pagination={false}
              tableLayout="fixed"
              locale={{
                emptyText: (
                  <div className="empty-text">No previous CSV uploads found.</div>
                ),
              }}
              sticky
            />
          </div>

          <div className="csv-history-footer">
            <Pagination
              disabled={total === 0}
              current={safeCurrent}
              pageSize={pageSize}
              total={total}
              showTotal={(t, range) =>
                total > 0 ? `${range[0]}-${range[1]} of ${total}` : "0-0 of 0"
              }
              onChange={(page, size) => {
                setCurrent(page);
                if (size !== pageSize) setPageSize(size);
              }}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default CsvUploadForm;
